import { useEffect, useRef, useState } from 'react'

interface Props {
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (next: string[]) => void
}

export function ColumnFilterDropdown({ options, selected, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const active = selected.length > 0 && selected.length < options.length
  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase().trim()))
    : options

  const toggle = (value: string) => {
    const next = new Set(selected)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    onChange(Array.from(next))
  }
  const selectAll = () => onChange(options.map((o) => o.value))
  const clearAll = () => onChange([])

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`p-1 rounded hover:bg-gray-100 transition-colors ${
          active ? 'text-brand-600' : 'text-gray-400'
        }`}
        aria-label="Фильтр"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 4h18l-7 9v6l-4 2v-8L3 4z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[200px] text-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск..."
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded mb-2 outline-none focus:border-brand-500"
            autoFocus
          />
          <div className="flex gap-3 mb-2 pb-2 border-b border-gray-100">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs text-brand-600 hover:text-brand-700"
            >
              Выбрать все
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-brand-600 hover:text-brand-700"
            >
              Сбросить
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {filtered.length === 0 && (
              <div className="text-xs italic text-gray-400 px-1 py-0.5">Нет данных</div>
            )}
            {filtered.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggle(opt.value)}
                  className="cursor-pointer"
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
